// AI-SLOP

import { Progress, Spinner, Text } from "@overmap-ai/blocks"
import { cx } from "class-variance-authority"
import { memo, useEffect, useState } from "react"

const STEPS = [
	"Sending your question",
	"Collecting relevant context",
	"Asking the model",
	"Preparing response",
] as const

type Step = (typeof STEPS)[number]

const STEP_TIMEOUTS: Record<Step, number> = {
	"Asking the model": 1000,
	"Collecting relevant context": 2000,
	"Preparing response": 1000,
	"Sending your question": 1000,
}

export const LoadingSteps = memo(() => {
	const [stepIndex, setStepIndex] = useState<number>(0)

	const percent = Math.min(99, Math.round(((stepIndex + 1) / STEPS.length) * 100))

	useEffect(() => {
		let isActive = true
		let timeoutId: number | undefined

		const advance = (index: number) => {
			if (!isActive) return
			if (index >= STEPS.length - 1) return

			const step = STEPS[index]
			const timeout = STEP_TIMEOUTS[step]

			timeoutId = window.setTimeout(() => {
				if (!isActive) return
				setStepIndex((prev) => Math.min(prev + 1, STEPS.length - 1))
				advance(index + 1)
			}, timeout)
		}

		advance(0)

		return () => {
			isActive = false
			if (timeoutId) window.clearTimeout(timeoutId)
		}
	}, [])

	return (
		<div className="w-full">
			<div className="text-(--base-a11) mb-2 flex items-center justify-between">
				<Text weight="medium" accentColor="base">
					Working on it…
				</Text>

				<div className="flex items-center gap-2">
					<Text size="sm" accentColor="base">
						{percent}%
					</Text>

					<Spinner />
				</div>
			</div>

			<Progress value={percent} />

			<ul className="mt-2 flex flex-col gap-2">
				{STEPS.map((label, index) => (
					<li key={label} className="text-(--base-a11) flex items-center gap-2">
						<div
							className={cx(
								"size-4 rounded-full border",
								index < stepIndex && "border-(--base-a7) bg-(--base-a7)",
								index === stepIndex && "border-(--base-a8) animate-pulse",
								index > stepIndex && "border-(--base-a5)",
							)}
						/>

						<Text size="sm" accentColor="base">
							{label}
						</Text>
					</li>
				))}
			</ul>
		</div>
	)
})

LoadingSteps.displayName = "LoadingSteps"
